import React from 'react';
import {Link, useParams} from 'react-router-dom';
import {findReportBySlug} from './reportCatalogue';
import MaterialsRevenue from './MaterialsRevenue';
import StudentSubjectOrders from './StudentSubjectOrders';
import TuitionRevenue from './TuitionRevenue';
import CreditNotes from './CreditNotes';
import LeadFunnel from './LeadFunnel';
import LeadSources from './LeadSources';
import LeadVelocity from './LeadVelocity';
import OrderVolume from './OrderVolume';
import ItemDemand from './ItemDemand';
import StudentWorkOrders from './StudentWorkOrders';
import PrintVolume from './PrintVolume';
import AdhocPrints from './AdhocPrints';
import ThresholdBreaches from './ThresholdBreaches';

/**
 * Implemented reports, keyed by slug. A report becomes real by appearing here
 * and flipping its catalogue status to 'live'; everything else falls through to
 * the placeholder below, so no row on the index is ever a dead end.
 */
const IMPLEMENTED: Record<string, React.ComponentType> = {
  'materials-revenue': MaterialsRevenue,
  'student-subject-orders': StudentSubjectOrders,
  'tuition-revenue': TuitionRevenue,
  'credit-notes': CreditNotes,
  'lead-funnel': LeadFunnel,
  'lead-sources': LeadSources,
  'lead-velocity': LeadVelocity,
  'order-volume': OrderVolume,
  'item-demand': ItemDemand,
  'student-work-orders': StudentWorkOrders,
  'print-volume': PrintVolume,
  'adhoc-prints': AdhocPrints,
  'threshold-breaches': ThresholdBreaches
};

/**
 * Shell for an individual report.
 *
 * Every report in the catalogue routes here, so no row on the index leads to a
 * dead end. As each report is implemented its component is mounted in place of
 * the placeholder below and its catalogue entry flips to status 'live'.
 *
 * The anatomy each report fills in is the same one throughout the wireframe:
 * filter bar, KPI strip, chart, drill-down table, Excel export.
 */
const ReportPage = () => {
  const {slug} = useParams();
  const report = findReportBySlug(slug);

  if (!report) {
    return (
      <div>
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Report not found</h1>
              </div>
            </div>
          </div>
        </section>
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <p className="text-muted">
                  There is no report at this address.
                </p>
                <Link to="/reports" className="btn btn-primary">
                  Back to all reports
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const Implemented = IMPLEMENTED[report.slug];
  if (Implemented) return <Implemented />;

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{report.name}</h1>
              <p className="text-muted mb-0">{report.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/reports">Reports</Link>
                </li>
                <li className="breadcrumb-item active">{report.name}</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title text-capitalize">
                <span className="badge badge-light border mr-2">
                  {report.code}
                </span>
                {report.name}
              </h3>
            </div>
            <div className="card-body text-center py-5">
              <i className="fas fa-chart-pie fa-3x text-muted mb-3 d-block" />
              <h5>This report is being built</h5>
              <p className="text-muted mb-4">
                It will show filters, headline figures, a chart and a full
                breakdown you can export to Excel.
              </p>
              <Link to="/reports" className="btn btn-outline-primary">
                Back to all reports
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReportPage;
